import type { CSSProperties, ReactNode } from "react";
import { useFonts } from "./bits";
import { Nav, type NavCta } from "./Hero";
import { Footer } from "./SectionsB";
import { EventStrip } from "./EventStrip";
import { Part, rootStyle, type PartName } from "./parts";
import type { PageKey } from "./bits";
import "./Page.css";

/* The shared frame for blue-2's inner pages: event bar, a page header that
   carries the nav, the page's sections, and the footer. Sections wrap
   themselves in <Part> to take a Team mix palette (see parts.tsx).

   Each page picks a header `tone` from the palette so pages don't all open on
   the same blue. A tone overrides the header's role tokens: --k-hero is the
   background, strong-ink the text, strong-pop the doodles and big highlights,
   head-label small accent text (kickers, the current nav link) at 4.5:1 or
   more, soft the giant display word (drawn with a strong-ink offset shadow),
   and cta / cta-ink the header's buttons. Light tones also get a softer
   background shade (see Page.css). */

const INK = "#0B302A", CREAM = "#FBF3E4";
export const TONES = {
  blue:       { "--k-hero": "#05649B", "--k-strong-ink": CREAM, "--k-strong-pop": "#F0BE3C", "--k-head-label": "#F7D883", "--k-soft": "#F0B0B0", "--k-cta": "#F0BE3C", "--k-cta-ink": INK },
  teal:       { "--k-hero": "#145A50", "--k-strong-ink": CREAM, "--k-strong-pop": "#F0BE3C", "--k-head-label": "#F0BE3C", "--k-soft": "#F0B0B0", "--k-cta": "#F0BE3C", "--k-cta-ink": INK },
  coral:      { "--k-hero": "#E66E78", "--k-strong-ink": INK, "--k-strong-pop": CREAM, "--k-head-label": INK, "--k-soft": CREAM, "--k-cta": INK, "--k-cta-ink": CREAM },
  gold:       { "--k-hero": "#F0BE3C", "--k-strong-ink": INK, "--k-strong-pop": "#C83214", "--k-head-label": "#145A50", "--k-soft": "#C83214", "--k-cta": INK, "--k-cta-ink": CREAM },
  periwinkle: { "--k-hero": "#C0C7FB", "--k-strong-ink": INK, "--k-strong-pop": "#C83214", "--k-head-label": "#145A82", "--k-soft": "#145A82", "--k-word-shadow": CREAM, "--k-cta": INK, "--k-cta-ink": CREAM },
  blush:      { "--k-hero": "#F0B0B0", "--k-strong-ink": INK, "--k-strong-pop": "#C83214", "--k-head-label": "#8E2310", "--k-soft": CREAM, "--k-cta": INK, "--k-cta-ink": CREAM },
} as const;
const LIGHT = new Set<string>(["coral", "gold", "periwinkle", "blush"]);
export type Tone = keyof typeof TONES;

/** Optional extras (all backwards-compatible): `cta` swaps the header button,
    `sub` renders right under the nav inside the header (e.g. a phone-only
    sub-nav), `className` is added to the page root. */
export function Blue2Page({ current, tone = "blue", header, children, cta, sub, className }: {
  current?: PageKey; tone?: Tone; header?: ReactNode; children: ReactNode; cta?: NavCta; sub?: ReactNode; className?: string;
}) {
  useFonts();
  return <div className={`h5 b2 b2-page${className ? ` ${className}` : ""}`} style={rootStyle}>
    <EventStrip />
    <Part p="hero">
      <section className={`b2-hero b2-pagehead b2-tone-${tone}${LIGHT.has(tone) ? " b2-tone-light" : ""}`} style={TONES[tone] as CSSProperties}>
        <div className="b2-pagehead-stage">
          <Nav current={current} cta={cta} />
          {sub}
          {header && <div className="b2-pagehead-body">{header}</div>}
        </div>
      </section>
    </Part>
    {children}
    <Part p="footer"><Footer /></Part>
  </div>;
}

export { Part };
export type { PartName, NavCta };
